import React, { useEffect, useState, useMemo, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Package, CheckCircle2, XCircle, Truck, RotateCcw, Clock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

/**
 * MaterialRequests — Storekeeper/Engineer/Manager queue.
 *
 * Displays every MR grouped by lifecycle stage (Pending, Ready to issue,
 * Fulfilled, Rejected) and lets the operator approve, reject, issue (with
 * per-line backorder → auto-PR), or mark returnable items returned.
 * All actions are optimistic + refetch on success.
 */

const STATUS_BADGE = {
  pending: { label: 'Pending approval', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  approved: { label: 'Ready to issue', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  partially_issued: { label: 'Partially issued', color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  issued: { label: 'Fulfilled', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  rejected: { label: 'Rejected', color: 'bg-rose-100 text-rose-800 border-rose-300' },
};

const LINE_STATUS = {
  pending: 'text-amber-600',
  approved: 'text-blue-600',
  issued: 'text-emerald-600',
  backordered: 'text-purple-600',
  rejected: 'text-rose-600',
  returned: 'text-slate-500',
};

export default function MaterialRequests() {
  const { user } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null); // mrid being rejected
  const [rejectReason, setRejectReason] = useState('');

  const canApprove = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ENGINEER', 'CS_SUPERVISOR'].includes(user?.role);
  const canIssue = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'STOREKEEPER'].includes(user?.role);
  const canReturn = canIssue;

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/material-requests`, { withCredentials: true });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load material requests');
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const grouped = useMemo(() => {
    const g = { pending: [], approved: [], partially_issued: [], issued: [], rejected: [] };
    for (const r of rows) (g[r.status] || g.pending).push(r);
    return g;
  }, [rows]);

  const approve = async (mr) => {
    try {
      await axios.put(`${API}/material-requests/${mr.id}/approve`, {}, { withCredentials: true });
      toast.success(`${mr.mr_number} approved — ready for the storekeeper.`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) return toast.error('Please provide a reason.');
    try {
      await axios.put(`${API}/material-requests/${rejecting}/reject`, { reason: rejectReason }, { withCredentials: true });
      toast.success('Rejected');
      setRejecting(null); setRejectReason('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  };

  const issue = async (mr, backorderIndices = []) => {
    try {
      const r = await axios.put(`${API}/material-requests/${mr.id}/issue`, { backorder_indices: backorderIndices }, { withCredentials: true });
      toast.success(`Issued ${r.data.issued} item(s). Stock updated.`);
      fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Backend returned out_of_stock — offer to auto-create PR
      const idxs = detail && detail.out_of_stock_indices;
      if (Array.isArray(idxs) && idxs.length > 0) {
        if (window.confirm(`${idxs.length} line(s) are short. Auto-create a Purchase Request for the shortfall?`)) {
          issue(mr, idxs);
        }
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Issue failed');
      }
    }
  };

  const markReturned = async (mr, idx) => {
    try {
      await axios.put(`${API}/material-requests/${mr.id}/return-line/${idx}`, {}, { withCredentials: true });
      toast.success('Marked as returned. Stock restored.');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Return failed'); }
  };

  return (
    <Layout>
      <div data-testid="material-requests-page" className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <Package className="w-6 h-6 text-purple-600" />Material Requests
          </h1>
          <p className="text-sm text-muted-foreground">
            Requests raised by technicians from their Work Orders. Approve, issue from stock (or auto-PR the shortfall), and track returns.
          </p>
        </div>

        {loading && <Card className="p-8 text-center text-muted-foreground">Loading…</Card>}
        {!loading && rows.length === 0 && (
          <Card className="p-8 text-center text-muted-foreground">
            <Package className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            No material requests yet. Technicians raise them from a Work Order.
          </Card>
        )}

        {[
          { key: 'pending', title: 'Awaiting approval', hint: 'Engineer / Manager reviews and clears each line.' },
          { key: 'approved', title: 'Ready to issue', hint: 'Storekeeper issues from stock. Short lines auto-become PRs.' },
          { key: 'partially_issued', title: 'Partially issued' },
          { key: 'issued', title: 'Fulfilled' },
          { key: 'rejected', title: 'Rejected' },
        ].map(section => {
          const list = grouped[section.key] || [];
          if (list.length === 0) return null;
          return (
            <div key={section.key} data-testid={`mr-section-${section.key}`}>
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="text-lg font-semibold text-foreground">{section.title} <span className="text-sm text-muted-foreground">· {list.length}</span></h2>
                {section.hint && <span className="text-xs text-muted-foreground">{section.hint}</span>}
              </div>
              <div className="space-y-3">
                {list.map(mr => {
                  const badge = STATUS_BADGE[mr.status] || STATUS_BADGE.pending;
                  return (
                    <Card key={mr.id} data-testid={`mr-card-${mr.mr_number}`} className="p-5">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-sm font-semibold">{mr.mr_number}</span>
                            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${badge.color}`}>{badge.label}</span>
                            {mr.work_order_number && (
                              <span className="text-xs text-slate-500">WO {mr.work_order_number}</span>
                            )}
                            {mr.ticket_number && (
                              <span className="text-xs text-slate-500">· {mr.ticket_number}</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            Requested by {mr.requested_by_name || '—'} · {mr.requested_at ? new Date(mr.requested_at).toLocaleString() : ''}
                          </div>
                          {mr.reason && <div className="text-xs italic text-slate-500 mt-1">&ldquo;{mr.reason}&rdquo;</div>}
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {mr.status === 'pending' && canApprove && (
                            <>
                              <Button data-testid={`mr-approve-${mr.mr_number}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approve(mr)}>
                                <CheckCircle2 className="w-4 h-4 mr-1" />Approve
                              </Button>
                              <Button data-testid={`mr-reject-${mr.mr_number}`} size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setRejecting(mr.id)}>
                                <XCircle className="w-4 h-4 mr-1" />Reject
                              </Button>
                            </>
                          )}
                          {['approved', 'partially_issued'].includes(mr.status) && canIssue && (
                            <Button data-testid={`mr-issue-${mr.mr_number}`} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => issue(mr)}>
                              <Truck className="w-4 h-4 mr-1" />Issue from stock
                            </Button>
                          )}
                        </div>
                      </div>

                      {rejecting === mr.id && (
                        <div className="mb-3 p-3 rounded-lg bg-rose-50 border border-rose-200">
                          <Input
                            data-testid={`mr-reject-reason-${mr.mr_number}`}
                            placeholder="Why is this rejected?" value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            className="mb-2"
                          />
                          <div className="flex gap-2">
                            <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={reject} data-testid={`mr-confirm-reject-${mr.mr_number}`}>Confirm reject</Button>
                            <Button size="sm" variant="ghost" onClick={() => { setRejecting(null); setRejectReason(''); }}>Cancel</Button>
                          </div>
                        </div>
                      )}

                      <div className="border-t pt-3">
                        <div className="grid grid-cols-12 gap-2 text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                          <div className="col-span-5">Item</div>
                          <div className="col-span-2">Qty</div>
                          <div className="col-span-2">Returnable?</div>
                          <div className="col-span-2">Status</div>
                          <div className="col-span-1"></div>
                        </div>
                        {(mr.lines || []).map((l, i) => (
                          <div key={i} className="grid grid-cols-12 gap-2 py-1.5 text-sm items-center border-t border-slate-100">
                            <div className="col-span-5">
                              {l.item_name}
                              {(l.brand || l.size) && <span className="text-xs text-slate-500 ml-1">· {[l.brand, l.size].filter(Boolean).join(' · ')}</span>}
                              {l.notes && <div className="text-xs text-slate-500 italic">{l.notes}</div>}
                            </div>
                            <div className="col-span-2">{l.qty} {l.unit || ''}</div>
                            <div className="col-span-2 text-xs">{l.is_returnable ? '🔁 Returnable' : 'Consumable'}</div>
                            <div className={`col-span-2 text-xs font-semibold uppercase ${LINE_STATUS[l.status] || 'text-slate-500'}`}>{l.status}
                              {l.pr_number && (
                                <a
                                  href={`/purchase-requests?highlight=${encodeURIComponent(l.pr_number)}&from=${encodeURIComponent(mr.mr_number)}`}
                                  className="block text-[10px] text-purple-600 hover:text-purple-800 hover:underline"
                                  title="Open the linked Purchase Request"
                                  data-testid={`mr-pr-link-${l.pr_number}`}
                                >→ {l.pr_number}</a>
                              )}
                            </div>
                            <div className="col-span-1 text-right">
                              {l.status === 'issued' && l.is_returnable && canReturn && (
                                <button
                                  data-testid={`mr-return-line-${mr.mr_number}-${i}`}
                                  onClick={() => markReturned(mr, i)}
                                  className="text-xs text-emerald-700 hover:text-emerald-900"
                                  title="Mark returned + restore stock"
                                >
                                  <RotateCcw className="w-3 h-3 inline" /> Return
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
